<template>
    <div class="wrapper" v-if="!noDataFound && props.search">
        <!-- Tab headings -->
        <Swiper class="nav nav-tabs search-tabs align-items-end gap-medium" role="tablist" tag="ul" v-if="!contentLoading">
            <template #content>
                <li class="nav-item d-flex align-items-center h-100" role="presentation" v-for="(key, index) in Object.keys(tabsAndData)">
                    <button class="nav-link h-100 p-0 text-capitalize" :class="index == 0 ? 'active' : ''" v-ripple data-bs-toggle="tab" :data-bs-target="`#tab-${index}-pane-${sizeClass}`" type="button" role="tab" @click="setActiveTab(key)">{{ key.replace(/_/g, ' ') }}
                    </button>
                </li>
            </template>
        </Swiper>

        <!-- Tabs content -->
        <div class="tab-content search-results-container py-3" :class="sizeClass" v-if="!contentLoading">
            <SearchTab v-for="(key, index) in Object.keys(tabsAndData)" :tab-index="index" :size-class="sizeClass" :tab-key="key" :tab-data="tabsAndData[key].data" :loading-data="tabsAndData[key].loading" :all-loaded="tabsAndData[key].all_loaded" :key="tabsAndData[key].data.tabIndex + '-' + key" @load-more="(val) => loadSearchContent(val)" :keyword="props.search" :active-Tab="activeTab"></SearchTab>
        </div>
    </div>
    <div class="px-4 py-3 text-muted" v-else-if="noDataFound">
        Sorry, no results were found for <b>"{{ props.search }}".</b>
    </div>
    <!-- <div class="px-4 py-3 text-muted" v-else>
        Type something to search the database
    </div> -->
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import Tab from 'bootstrap/js/dist/tab'
import Swiper from "../Swiper.vue";
import SearchTab from "@/components/Search/SearchTab.vue";
import offcanvas from 'bootstrap/js/dist/offcanvas';
import { useDebounceFn } from "@vueuse/core";
import apiClient from "@/utils/axios";
import { useHttpErrorManager } from '@/composables/useHttpErrorManager';
import { useAppHelpers } from '@/composables/useAppHelpers';
import * as Sentry from '@sentry/vue';

const helper = useAppHelpers();

const props = defineProps({
    sizeClass: String,
    search: String
});

const emits = defineEmits(['loading-content']);

const tabsAndData = ref({});
const tabsExtraKey = ref({
    'medications': [{key: 'rating', iconType: 'bootstrap', icon: 'bi-star-fill', iconColor: 'text-warning'}],
    'conditions': [{key: 'rating', iconType: 'bootstrap', icon: 'bi-star-fill', iconColor: 'text-warning'}],
    'therapies': null,
    'categories': null,
    'side_effects': [{key: 'severity', iconType: 'bootstrap', icon: 'bi-fire', iconColor: 'text-danger'}],
    'symptoms': null
});
const itemsToLoad = ref(20);
const params = ref({});
const totalIndex = ref(1);
const activeTab = ref('all');
const noDataFound = ref(false);

const setActiveTab = (val) => {
    activeTab.value = val;
}

const loadSearchContent = async (url = 'medications') => {
    if(url == 'all' || !props.search){
        return;
    }

    if ((tabsAndData.value[url] && !tabsAndData.value[url].all_loaded) || !tabsAndData.value[url]) {
        if (!tabsAndData.value[url]) {
            totalIndex.value = totalIndex.value + 1;
            tabsAndData.value[url] = {
                data: [],
                all_loaded: false,
                index: totalIndex.value,
                loading: true
            }
        }
        tabsAndData.value[url].loading = true; 

        try {
            params.value['limit'] = itemsToLoad.value;
            params.value['offset'] = tabsAndData.value[url].data.length;
            params.value['filter'] = props.search;

            let dataSet = [];
            let allContentLoaded = false;

            const content = await apiClient.get(url, { params: params.value });

            if (content.data.data) {
                let contentLoaded = content.data.data.length;
                if (contentLoaded == 0 || contentLoaded < itemsToLoad.value) {
                    allContentLoaded = true;
                }
                if (tabsAndData.value[url]) {
                    dataSet = tabsAndData.value[url].data;
                    content.data.data.forEach((d) => {
                        d['tabType'] = url;
                        d['dataExtraKey'] = tabsExtraKey.value[url] ? tabsExtraKey.value[url] : null;
                        dataSet.push(d);
                        tabsAndData.value['all'].data.push(d);
                    });
                    tabsAndData.value[url].data = dataSet;
                    tabsAndData.value[url].all_loaded = allContentLoaded;

                }
            }

            tabsAndData.value[url].loading = false;
            if(tabsAndData.value[url].data.length == 0){
                delete tabsAndData.value[url];
                totalIndex.value = totalIndex.value - 1;
            }

            if(tabsAndData.value['all'].data.length == 0){
                noDataFound.value = true;
            }
            else{
                noDataFound.value = false;
            }
        }
        catch (error) {
			Sentry.captureException(error);
			Sentry.captureMessage('Error in loading search content', {
				'tabsAndData': tabsAndData.value,
				'url': url,
			});
            tabsAndData.value[url].loading = false;
            let errorObj = await useHttpErrorManager().handleError(error, false);
        }
    }
}

const debounceSearch = useDebounceFn(async () => {
    // if (!contentLoading.value) {
        contentLoading.value = true;
        emits('loading-content', contentLoading.value);
        tabsAndData.value = {'all' : { data: [], all_loaded: true, index: 1, loading: false }};
        totalIndex.value = 1;
        noDataFound.value = false;

        if(props.search && props.search.trim().length > 0){
            await loadSearchContent('medications');
            await loadSearchContent('conditions');
            await loadSearchContent('therapies');
            await loadSearchContent('categories');
            await loadSearchContent('side_effects');
            await loadSearchContent('symptoms');
        }
        contentLoading.value = false;
        emits('loading-content', contentLoading.value);
    // }
}, 500);

const contentLoading = ref(false);
watch(() => props.search, async (val) => {
    if(val){
        debounceSearch();
    }
    
});

onMounted(async () => {
    if(props.search && props.search.trim().length > 0){
        contentLoading.value = true;
        emits('loading-content', contentLoading.value);
        tabsAndData.value = {'all' : { data: [], all_loaded: true, index: 1, loading: false }};
        totalIndex.value = 1;
        noDataFound.value = false;

        await loadSearchContent('medications');
        await loadSearchContent('conditions');
        await loadSearchContent('therapies');
        await loadSearchContent('categories');
        await loadSearchContent('side_effects');
        await loadSearchContent('symptoms');

        contentLoading.value = false;
        emits('loading-content', contentLoading.value);
    }
});

</script>

<style scoped>
.wrapper {
    border-radius: inherit;
}
</style>